/**
 * Os orçamentos, em tabela.
 *
 * Papel: mostra uma linha por proposta, com número, data, casa, fornecedor,
 * status e total, cada uma na própria coluna — inclusive Casa, que não é
 * opcional: todo `orcamento.residencia` vem preenchido, e é o que separa a
 * proposta de uma casa das de outra.
 *
 * Como usar: quem monta a tabela passa as propostas já filtradas — a seleção por
 * situação é de quem mostra, e trazê-la para cá obrigaria a inventar um filtro
 * por chamador.
 *
 * O total é `totalDoOrcamento` sobre os itens e o desconto, e não um campo da
 * proposta. A linha que teve desconto mostra a soma dos itens abaixo do total,
 * porque um total menor que a soma não se explica sozinho.
 */
import { type Orcamento, totalDoOrcamento } from '@concierge/shared';

import { formatarData, formatarDinheiro } from '@/shared/lib/formato';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ROTULOS_STATUS, TOM_DO_STATUS } from '../rotulos';

interface TabelaOrcamentosProps {
  orcamentos: Orcamento[];
  /** Chamado com o orçamento da linha; ausente esconde a coluna de ações. */
  onAbrir?: (orcamento: Orcamento) => void;
  /** Chamado com o orçamento a excluir; a confirmação é de quem monta. */
  onRemover?: (orcamento: Orcamento) => void;
}

/**
 * A tabela de orçamentos.
 *
 * @param orcamentos - As propostas a mostrar, já na ordem e no recorte desejados.
 * @param onAbrir - Chamado ao clicar em abrir; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em excluir; ausente esconde as ações.
 * @returns A tabela com uma linha por proposta.
 */
export function TabelaOrcamentos({ orcamentos, onAbrir, onRemover }: TabelaOrcamentosProps) {
  const mostrarAcoes = onAbrir !== undefined || onRemover !== undefined;

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Número</TableHead>
            <TableHead>Data</TableHead>
            <TableHead>Casa</TableHead>
            <TableHead>Fornecedor</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Total</TableHead>
            {mostrarAcoes && (
              <TableHead>
                <span className="sr-only">Ações</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {orcamentos.map((orcamento) => {
            // A soma sem desconto é o mesmo `totalDoOrcamento` com o desconto
            // zerado: somar aqui à mão faria a linha discordar do total no dia em
            // que a fórmula ganhar mais um termo.
            const soma = totalDoOrcamento(orcamento.itens, 0);
            const total = totalDoOrcamento(orcamento.itens, orcamento.desconto);

            return (
              <TableRow key={orcamento.id}>
                <TableCell className="tabular">{orcamento.numero || '—'}</TableCell>
                <TableCell className="tabular">{formatarData(orcamento.data)}</TableCell>
                <TableCell>{orcamento.residencia.apelido}</TableCell>
                <TableCell className="text-texto-medio">
                  {orcamento.fornecedor || '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={TOM_DO_STATUS[orcamento.status]} className="w-fit">
                    {ROTULOS_STATUS[orcamento.status]}
                  </Badge>
                </TableCell>
                <TableCell className="tabular">
                  {formatarDinheiro(total)}
                  {orcamento.desconto > 0 && (
                    <span className="block text-xs text-texto-fraco">
                      itens {formatarDinheiro(soma)}
                    </span>
                  )}
                </TableCell>
                {mostrarAcoes && (
                  <TableCell>
                    <div className="flex gap-2">
                      {onAbrir !== undefined && (
                        <Button variant="outline" size="sm" onClick={() => onAbrir(orcamento)}>
                          abrir
                        </Button>
                      )}
                      {onRemover !== undefined && (
                        <Button variant="destructive" onClick={() => onRemover(orcamento)}>
                          remover
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
